/**
 * apps/battle/client/src/terminal/screens/SignupScreen.tsx
 *
 * Phase 4.2 M7 — self-serve signup.
 *
 * POST /api/auth/signup with { email, password, displayName, termsAccepted, ageAttested }.
 * On success the server sets the session cookie and returns the same shape as
 * /api/auth/login. We forward the student to the parent FSM via onSignupSuccess()
 * which transitions to a "verify email sent" screen and bootstraps the game.
 */
import React, { useState } from 'react';
import { api, ApiError } from '../../api.js';
import type { LoginResp, UserRole } from '../../api.js';
import { Spinner, ErrorBanner } from '../../widgets.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';

interface SignupScreenProps {
  onSignupSuccess: (student: { id: string; email: string; name: string; role: UserRole }) => void;
  onBackToLogin: () => void;
}

export function SignupScreen({ onSignupSuccess, onBackToLogin }: SignupScreenProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [terms, setTerms] = useState(false);
  const [ageAttested, setAgeAttested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function clientValidate(): string | null {
    if (!email.trim() || !email.includes('@')) return 'Enter a valid email.';
    if (!displayName.trim() || displayName.trim().length < 2) return 'Display name must be at least 2 characters.';
    if (password.length < 10) return 'Password must be at least 10 characters.';
    if (password !== confirm) return 'Passwords do not match.';
    if (!terms) return 'You must accept the terms to create an account.';
    if (!ageAttested) return 'You must confirm you are 13 or older.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = clientValidate();
    if (v) { setError(v); return; }
    setError(null);
    setLoading(true);
    try {
      const resp = await api.post<LoginResp>('/api/auth/signup', {
        email: email.trim(),
        password,
        displayName: displayName.trim(),
        termsAccepted: terms,
        ageAttested,
      });
      if (resp.authenticated && resp.student) {
        onSignupSuccess(resp.student);
      } else {
        setError('Signup failed — unexpected response.');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('An account with that email already exists. Try signing in.');
      } else if (err instanceof ApiError && err.status === 422) {
        // Server password-policy reject
        const body = err.body as { error?: string; reason?: string } | undefined;
        setError(body?.reason || 'That password is too weak — choose a stronger one.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else if (err instanceof ApiError && err.status === 503) {
        setError('Service unavailable. Please try again later.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'create account' },
    { label: 'Esc', description: 'back to login', onActivate: onBackToLogin },
  ];

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;CREATE ACCOUNT</>}
      keybar={<KeyBar items={keybar} />}
    >
      <form onSubmit={handleSubmit} style={{ maxWidth: 480, margin: '0 auto', paddingTop: 16 }}>
        <p style={{ color: '#97a3b6', marginTop: 0, marginBottom: 16, fontSize: 13 }}>
          Already have an account?{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); onBackToLogin(); }} style={{ color: '#3b82f6' }}>
            Sign in
          </a>
        </p>

        <div className="form-field">
          <label className="form-label" htmlFor="su-email">Email</label>
          <input
            id="su-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="su-name">Display name</label>
          <input
            id="su-name"
            type="text"
            autoComplete="nickname"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="What other players will see"
            maxLength={32}
            required
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="su-pw">Password</label>
          <input
            id="su-pw"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 10 characters"
            minLength={10}
            required
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="su-pw2">Confirm password</label>
          <input
            id="su-pw2"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat password"
            minLength={10}
            required
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13, color: '#cdd5e0' }}>
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>
            I agree to the <a href="/terms" target="_blank" style={{ color: '#3b82f6' }}>Terms</a> and{' '}
            <a href="/privacy" target="_blank" style={{ color: '#3b82f6' }}>Privacy Policy</a>.
          </span>
        </label>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 13, color: '#cdd5e0' }}>
          <input type="checkbox" checked={ageAttested} onChange={(e) => setAgeAttested(e.target.checked)} />
          <span>I confirm I am 13 years of age or older.</span>
        </label>

        {error && <div style={{ marginTop: 12 }}><ErrorBanner msg={error} /></div>}

        <button className="btn btn-primary w-full mt-4" type="submit" disabled={loading}>
          {loading ? <Spinner size={16} /> : 'Create account'}
        </button>
      </form>
    </TerminalFrame>
  );
}
